/**
 * Helpers puros para la UI de progreso de actualización de un agente.
 *
 * Por qué existen: el agente reporta el progreso de la actualización por WS
 * (`agent.update.state`). Durante la fase de "applying" el agente se reinicia
 * a sí mismo, lo que provoca:
 *   - la conexión WS cae y el flag `agent.connected` queda en `false`
 *   - el último `percent` reportado es 90-95%, sin un `complete` inmediato
 *   - el operador ve "Offline" + barra pegada en ~90% durante varios segundos
 *
 * Además, el agente reporta `status: 'retrying', percent: 0` entre intentos
 * cuando hubo un fallo transitorio, lo que hace que el `LinearProgress` salte
 * de un valor alto a 0 y de `determinate` a `indeterminate` (flicker visible).
 *
 * Estos helpers deciden qué mostrar (label, hint, fase) y un `percent`
 * monotónico para que la barra sólo avance, sin saltos.
 */

import type { Agent, AgentUpdateState } from '@/types/agent';
import { compareVersions } from '@/lib/notifications/buildNotifications';

export type UpdatePhase =
  | 'idle'
  | 'progress'
  | 'waiting-restart'
  | 'waiting-stalled'
  | 'retrying'
  | 'complete'
  | 'already-up-to-date'
  | 'error';

export interface ComputeUpdateDisplayInput {
  state: AgentUpdateState | null;
  agent: Pick<Agent, 'agentId' | 'connected' | 'version'> | null;
  /** Versión "latest" del manifiesto. Si se pasa, ayuda a detectar el caso
   *  "complete perdido" (agente volvió online con la versión nueva pero el
   *  estado sigue en applying). */
  latestVersion?: string;
  /** `Date.now()` (o mock) — inyectado para que la lógica sea testeable. */
  now: number;
  options?: SmoothUpdateStateOptions;
}

export interface SmoothUpdateStateOptions {
  /** ms sin updates tras los cuales un update en curso se considera "stalled".
   *  Default: 60_000. */
  stallTimeoutMs?: number;
}

export interface UpdateDisplay {
  phase: UpdatePhase;
  /** Percent monotónico 0..100 que la UI debe usar para la barra. */
  percent: number;
  /** Label principal que ve el operador. */
  label: string;
  /** Hint secundario (una línea bajo el label). Opcional. */
  hint?: string | undefined;
  /** Mensaje de error si `phase === 'error'`. */
  error?: string | undefined;
  /** Número de intentos si `phase === 'error'`. */
  retryCount?: number | undefined;
  /** True si la barra debe renderizarse como `determinate` (vs. `indeterminate`). */
  determinate: boolean;
  /** True si la fase requiere mostrar un estado "paciente" — útil para que el
   *  padre deshabilite el botón de "Actualizar" o muestre un spinner. */
  blocking: boolean;
}

export const DEFAULT_STATUS_LABEL: Record<string, string> = {
  checking: 'Verificando…',
  downloading: 'Descargando…',
  verifying: 'Verificando checksum…',
  applying: 'Aplicando…',
  restarting: 'Verificando reinicio…',
  retrying: 'Reintentando…',
  complete: 'Actualizado',
  already_up_to_date: 'Ya actualizado',
  error: 'Error',
};

/** Acceso seguro al label por status. Devuelve el status crudo si no hay
 *  mapping (defensa contra statuses nuevos que el front aún no conoce). */
export function labelForStatus(status: string): string {
  return DEFAULT_STATUS_LABEL[status] ?? status;
}

const ACTIVE_STATUSES = new Set([
  'checking',
  'downloading',
  'verifying',
  'applying',
  'restarting',
  'retrying',
]);

/**
 * Versión pura: dado un estado de update + agente + reloj actual, devuelve
 * el "display" que la UI debe renderizar. Sin efectos ni estado React.
 *
 * El caller (hook) es responsable de trackear el percent monotónico entre
 * renders. Esta función sólo clasifica la fase y devuelve el `percent` actual.
 */
export function computeUpdateDisplay(input: ComputeUpdateDisplayInput): UpdateDisplay | null {
  const { state, agent, latestVersion, now } = input;
  const { stallTimeoutMs = 60_000 } = input.options ?? {};

  if (!state) return null;

  const status = state.status;

  // 1) Estados terminales.
  if (status === 'error') {
    return {
      phase: 'error',
      percent: 0,
      label: 'Error',
      error: state.error,
      retryCount: state.retryCount,
      determinate: false,
      blocking: false,
    };
  }
  if (status === 'complete') {
    return {
      phase: 'complete',
      percent: 100,
      label: labelForStatus('complete'),
      determinate: true,
      blocking: false,
    };
  }
  if (status === 'already_up_to_date') {
    return {
      phase: 'already-up-to-date',
      percent: 100,
      label: labelForStatus('already_up_to_date'),
      determinate: true,
      blocking: false,
    };
  }

  // 2) Flujo activo. Decidir sub-fase.
  const age = now - new Date(state.updatedAt).getTime();
  const isApplying = status === 'applying' || status === 'restarting';
  const isOffline = !agent?.connected;
  const isRetry = status === 'retrying';

  // a) "Esperando reinicio" — el agente está aplicando y se reinició a sí
  //    mismo. La WS cayó, agent.connected está en false.
  if (isApplying && isOffline) {
    return {
      phase: 'waiting-restart',
      percent: Math.max(state.percent, 90), // nunca debajo de 90 al esperar reinicio
      label: 'Esperando reinicio del agente…',
      hint: 'El agente está reiniciando para completar la actualización.',
      determinate: true,
      blocking: true,
    };
  }

  // b) "Stalled" — sin updates hace rato, sin clear path a complete.
  if (age > stallTimeoutMs && ACTIVE_STATUSES.has(status)) {
    return {
      phase: 'waiting-stalled',
      percent: state.percent,
      label: 'Esperando al agente…',
      hint: 'No se recibieron actualizaciones en el último minuto.',
      determinate: state.percent > 0,
      blocking: true,
    };
  }

  // c) "Retrying" — la barra NO debe bajar a 0. El caller mantiene el
  //    percent monotónico. Acá sólo decidimos label/hint.
  if (isRetry) {
    return {
      phase: 'retrying',
      percent: state.percent,
      label: labelForStatus('retrying'),
      hint: 'El agente volverá a intentarlo automáticamente.',
      determinate: state.percent > 0,
      blocking: true,
    };
  }

  // d) Caso especial: el agente está online y con la versión nueva, pero el
  //    estado del update sigue en "applying". El `complete` se perdió en el
  //    socket antes del exit. Confiamos en que el agente está OK.
  if (
    latestVersion &&
    agent?.connected &&
    agent.version &&
    status === 'applying' &&
    compareVersions(agent.version, latestVersion) >= 0
  ) {
    return {
      phase: 'complete',
      percent: 100,
      label: labelForStatus('complete'),
      hint: 'El agente ya reporta la nueva versión activa.',
      determinate: true,
      blocking: false,
    };
  }

  // e) Flujo activo estándar.
  return {
    phase: 'progress',
    percent: state.percent,
    label: labelForStatus(status),
    determinate: state.percent > 0,
    blocking: true,
  };
}

/** Helper: set de statuses "en curso". */
export function isActiveUpdateStatus(status: string | undefined | null): boolean {
  return !!status && ACTIVE_STATUSES.has(status);
}

/**
 * Versión efectiva del status de conexión del agente, considerando si está
 * en medio de un update. Usado por la UI para no mostrar "Offline" durante
 * el gap natural del reinicio (applying + WS caída).
 */
export type EffectiveConnection = 'online' | 'offline' | 'updating' | 'complete' | 'error';

export function effectiveConnection(
  agent: Pick<Agent, 'agentId' | 'connected' | 'version'> | null,
  state: AgentUpdateState | null
): EffectiveConnection {
  if (state) {
    if (state.status === 'error') return 'error';
    if (state.status === 'complete' || state.status === 'already_up_to_date') {
      return 'complete';
    }
    if (isActiveUpdateStatus(state.status)) return 'updating';
  }
  return agent?.connected ? 'online' : 'offline';
}
